import {
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useToast,
  keyframes,
  Link,
  Select,
  Progress
} from '@chakra-ui/react';
import Skeleton from 'react-loading-skeleton';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useAppContext, usePDFReportContext } from 'Context';
import useDocumentTitle from 'Helpers/useDocumentTitle';
import React, { useEffect, useState } from 'react';
import { calculatePercentageOfValues, formatToServerFormat } from 'Helpers/helpers';
import { FieldForecast, Field as FieldD, Demand, PDFFarmTotal, FarmSection } from 'Context/types';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import WeeklyPDFReport from 'Pages/PDFReporting/Components/WeeklyPDFReport';
import MonthlyPDFReport from 'Pages/PDFReporting/Components/MonthlyPDFReport';
import moment from 'moment';
import useDelayedLoadingState from 'Helpers/useDelayedLoadingState';
import { Calendar, Download } from 'react-feather';
import { isArray, set } from 'lodash';
import { Button, FilterDropDown } from 'Components';
import { DatePicker } from 'rsuite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { report } from 'process';

const PDFReporting = () => {
  const { trackPageView } = useMatomo();
  const { farm, varieties, variety, farms } = useAppContext();
  const {
    fields,
    fetchFieldsData,
    getVarietyForecastData,
    getVarietyHarvestData,
    getVarietyMilestoneForecastData,
    getDemand,
    loadingFields,
    loadingForecasts,
    loadingWeather,
    fetchWeatherData,
    weatherData,
    fetchFarmSections,
    farmSections
  } = usePDFReportContext();
  const toast = useToast();
  // Track page view
  useDocumentTitle('PDF Reporting | Lima Asili');

  useEffect(() => {
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [reportWeekNo, setReportWeekNo] = useState(moment().add('week', 1).isoWeek());
  const [reportLastWeekNo, setReportLastWeekNo] = useState(moment().add('week', 2).isoWeek());
  const [dateString, setDateString] = useState(
    moment().add('week', 1).startOf('isoWeek').format('DD-MMM-YYYY')
  );
  const [farmSection, setFarmSection] = useState<FarmSection | null>(null);
  const [varietyForecasts, setVarietyForecasts] = useState<any[]>([]);
  const [varietyHarvests, setVarietyHarvests] = useState<any[]>([]);
  const [varietyMilestoneForecasts, setVarietyMilestoneForecasts] = useState<any[]>([]);
  const [varietyDemand, setVarietyDemand] = useState<any[]>([]);
  const [totalFarmForecast, setTotalFarmForecast] = useState<number>(0);
  const [totalFarmHarvest, setTotalFarmHarvest] = useState<number>(0);
  const [totalFarmRejects, setTotalFarmRejects] = useState<number>(0);
  const [totalFarmDemand, setTotalFarmDemand] = useState<number>(0);
  const [farmTotals, setFarmTotals] = useState<PDFFarmTotal>({
    forecast: 0,
    harvest: 0,
    demand: 0,
    rejects: 0,
    weeks_out_1: 0,
    weeks_out_2: 0,
    active_fields_count: 0,
    total_fields_count: 0
  });
  const [farmSectionTotals, setFarmSectionTotals] = useState<PDFFarmTotal>({
    forecast: 0,
    harvest: 0,
    demand: 0,
    rejects: 0,
    weeks_out_1: 0,
    weeks_out_2: 0,
    active_fields_count: 0,
    total_fields_count: 0
  });
  const [varietyGraphData, setVarietyGraphData] = useState<any[]>([]);
  const [graphData, setGraphData] = useState<{
    farm: any;
    section: any;
    varieties: any[];
    milestone: any;
  }>({
    farm: {},
    section: {},
    varieties: [],
    milestone: {}
  });
  const [fieldForecasts, setFieldForecasts] = useState<Record<string, FieldForecast>>({});
  const [weatherGraphData, setWeatherGraphData] = useState<any[]>([]);
  const [weatherWeekOverview, setWeatherWeekOverview] = useState<any[]>([]);
  const [fieldsWithData, setFieldsWithData] = useState<FieldD[]>([]);
  // Recalculate farmSectionTotals whenever fieldsWithData, fields, or farmSection change
  useEffect(() => {
    if (!farmSection) return;
    // Calculate farm section totals using latest fieldsWithData and forecasts
    let forecast = 0;
    let harvest = 0;
    const demand = 0;
    const rejects = 0;
    const weeks_out_1 = 0;
    const weeks_out_2 = 0;
    // For each field in the selected section, sum up forecast and harvest from fieldForecasts
    fieldsWithData
      .filter((f) => f.farm_section_id === farmSection.id)
      .forEach((field) => {
        const key = field.name + '_' + field.variety_id;
        const ff = fieldForecasts[key];
        if (ff) {
          forecast += ff.forecasts?.[past_no_of_weeks] || 0;
          harvest += ff.harvests?.[past_no_of_weeks] || 0;
          // Add other fields if needed (demand, rejects, weeks_out_1, weeks_out_2)
        }
      });
    setFarmSectionTotals({
      forecast,
      harvest,
      demand,
      rejects,
      weeks_out_1,
      weeks_out_2,
      active_fields_count: fieldsWithData.filter((f) => f.farm_section_id === farmSection.id)
        .length,
      total_fields_count: fields.filter((f) => f.farm_section_id === farmSection.id).length
    });
  }, [fieldsWithData, fields, farmSection]);
  const [hidePdf, setHidePdf] = useState<boolean>(true);
  const isProcessing = useDelayedLoadingState({
    loadingState: loadingFields || loadingForecasts || loadingWeather,
    delayMs: 1000
  });

  const next_no_of_weeks = 2;
  const past_no_of_weeks = 2;

  const [weatherDays, setWeatherDays] = useState<moment.Moment[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [generationStartTime, setGenerationStartTime] = useState<number>(0);
  const [generationTime, setGenerationTime] = useState<string>('');
  const [batchProgress, setBatchProgress] = useState<string>('');

  // Helper function to batch promises in chunks to avoid overwhelming the server
  const batchPromises = async <T,>(
    promises: (() => Promise<T>)[],
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    batchSize: number = 15,
    onBatchComplete?: (completed: number, total: number) => void
  ): Promise<T[]> => {
    const results: T[] = [];
    for (let i = 0; i < promises.length; i += batchSize) {
      const batch = promises.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map((fn) => fn()));
      results.push(...batchResults);
      if (onBatchComplete) {
        onBatchComplete(Math.min(i + batchSize, promises.length), promises.length);
      }
    }
    return results;
  };

  const fetchFarmForecasts = async (
    report_date: Date,
    report_week_no: number,
    report_last_week_no: number,
    farm_section_id: number | null = null
  ) => {
    const startTime = Date.now();
    setGenerationStartTime(startTime);
    setIsGeneratingReport(true);
    setProgressPercentage(0);
    setGenerationTime('');
    setVarietyForecasts([]);
    setVarietyHarvests([]);
    setVarietyMilestoneForecasts([]);
    setVarietyDemand([]);
    setTotalFarmForecast(0);
    setTotalFarmHarvest(0);
    setTotalFarmRejects(0);
    setFarmTotals({
      forecast: 0,
      harvest: 0,
      demand: 0,
      rejects: 0,
      weeks_out_1: 0,
      weeks_out_2: 0,
      active_fields_count: 0,
      total_fields_count: 0
    });
    setFarmSectionTotals({
      forecast: 0,
      harvest: 0,
      demand: 0,
      rejects: 0,
      weeks_out_1: 0,
      weeks_out_2: 0,
      active_fields_count: 0,
      total_fields_count: 0
    });
    // set start and end date for next 4 weeks
    const start_date = formatToServerFormat(
      duration === 'weekly'
        ? moment(report_date).subtract(past_no_of_weeks, 'weeks').startOf('isoWeek').toDate()
        : moment(report_date).startOf('year').startOf('isoWeek').toDate()
    );
    const end_date = formatToServerFormat(
      duration === 'weekly'
        ? moment(report_date)
            .add('weeks', next_no_of_weeks)
            .endOf('isoWeek')
            .startOf('day')
            .toDate()
        : moment(report_date).endOf('year').endOf('isoWeek').toDate()
      // moment(report_date).endOf('month').endOf('isoWeek').toDate()
    );

    const variety_forecasts: any[] = [];
    const variety_harvests: any[] = [];
    const variety_milestones: any[] = [];
    const variety_demand: any[] = [];
    let total_forecasts = 0;
    let total_harvests = 0;
    let total_rejects = 0;
    let total_demand = 0;
    let total_weeks_out_1 = 0;
    let total_weeks_out_2 = 0;
    const farm_section_totals: PDFFarmTotal = {
      forecast: 0,
      harvest: 0,
      demand: 0,
      rejects: 0,
      weeks_out_1: 0,
      weeks_out_2: 0,
      active_fields_count: 0,
      total_fields_count: 0
    };

    // Determine what to fetch based on duration and customer
    const shouldFetchMilestones = duration === 'monthly' && farm?.customer?.id === 3;
    const shouldFetchDemand = duration === 'monthly';

    // Calculate total steps dynamically based on what we're fetching
    const baseSteps = 2; // forecast + harvest
    const optionalSteps = (shouldFetchMilestones ? 1 : 0) + (shouldFetchDemand ? 1 : 0);
    const totalSteps = baseSteps + optionalSteps + 2; // +2 for processing + graph preparation
    let currentStep = 0;

    // Update progress helper
    const updateProgress = () => {
      currentStep++;
      setProgressPercentage((currentStep / totalSteps) * 100);
    };

    // Fetch all variety data in parallel for performance
    // Use batching for large number of varieties to avoid overwhelming the server
    setProgressPercentage(5); // Starting fetches
    console.log(`Starting to fetch data for ${varieties.length} varieties`);

    const useBatching = varieties.length > 20; // Batch if more than 20 varieties
    const batchSize = 15; // Process 15 varieties at a time

    let forecastResults: any[];
    let harvestResults: any[];

    if (useBatching) {
      console.log(`Using batched requests with batch size ${batchSize}`);
      // Fetch forecasts in batches
      forecastResults = await batchPromises(
        varieties.map(
          (v) => () =>
            getVarietyForecastData('', v.id, start_date, end_date, null).catch((error) => {
              console.error(`Error fetching forecast for variety ${v.id}:`, error);
              return null;
            })
        ),
        batchSize,
        (completed, total) => {
          setBatchProgress(`Forecasts: ${completed}/${total}`);
        }
      );
      updateProgress();
      setBatchProgress('');
      console.log(`Completed ${forecastResults.length} forecast fetches`);

      // Fetch harvests in batches
      harvestResults = await batchPromises(
        varieties.map(
          (v) => () =>
            getVarietyHarvestData('', v.id, start_date, end_date, farm_section_id).catch(
              (error) => {
                console.error(`Error fetching harvest for variety ${v.id}:`, error);
                return null;
              }
            )
        ),
        batchSize,
        (completed, total) => {
          setBatchProgress(`Harvests: ${completed}/${total}`);
        }
      );
      updateProgress();
      setBatchProgress('');
      console.log(`Completed ${harvestResults.length} harvest fetches`);
    } else {
      // For smaller farms, fetch all at once
      const forecastPromises = varieties.map((v) =>
        getVarietyForecastData('', v.id, start_date, end_date, null).catch((error) => {
          console.error(`Error fetching forecast for variety ${v.id}:`, error);
          return null;
        })
      );
      const harvestPromises = varieties.map((v) =>
        getVarietyHarvestData('', v.id, start_date, end_date, farm_section_id).catch((error) => {
          console.error(`Error fetching harvest for variety ${v.id}:`, error);
          return null;
        })
      );

      [forecastResults, harvestResults] = await Promise.all([
        Promise.all(forecastPromises).then((results) => {
          updateProgress();
          return results;
        }),
        Promise.all(harvestPromises).then((results) => {
          updateProgress();
          return results;
        })
      ]);
    }

    // Fetch milestones and demand if needed
    let milestoneResults: any[] = [];
    let demandResults: any[] = [];

    // Only fetch milestones for monthly reports for customer 3
    if (shouldFetchMilestones) {
      if (useBatching) {
        milestoneResults = await batchPromises(
          varieties.map(
            (v) => () =>
              getVarietyMilestoneForecastData('', v.id, start_date, end_date).catch((error) => {
                console.error(`Error fetching milestones for variety ${v.id}:`, error);
                return null;
              })
          ),
          batchSize,
          (completed, total) => {
            setBatchProgress(`Milestones: ${completed}/${total}`);
          }
        );
        setBatchProgress('');
        console.log(`Completed ${milestoneResults.length} milestone fetches`);
      } else {
        const milestonePromises = varieties.map((v) =>
          getVarietyMilestoneForecastData('', v.id, start_date, end_date).catch((error) => {
            console.error(`Error fetching milestones for variety ${v.id}:`, error);
            return null;
          })
        );
        milestoneResults = await Promise.all(milestonePromises);
      }
      updateProgress();
    }

    // Only fetch demand for monthly reports
    if (shouldFetchDemand) {
      if (useBatching) {
        demandResults = await batchPromises(
          varieties.map(
            (v) => () =>
              getDemand(v.id, start_date, end_date).catch((error) => {
                console.error(`Error fetching demand for variety ${v.id}:`, error);
                return null;
              })
          ),
          batchSize,
          (completed, total) => {
            setBatchProgress(`Demand: ${completed}/${total}`);
          }
        );
        setBatchProgress('');
        console.log(`Completed ${demandResults.length} demand fetches`);
      } else {
        const demandPromises = varieties.map((v) =>
          getDemand(v.id, start_date, end_date).catch((error) => {
            console.error(`Error fetching demand for variety ${v.id}:`, error);
            return null;
          })
        );
        demandResults = await Promise.all(demandPromises);
      }
      updateProgress();
    }

    console.log('All fetch operations completed');

    console.log(
      `Processing ${varieties.length} varieties - Forecasts: ${forecastResults.length}, Harvests: ${harvestResults.length}`
    );

    for (let index = 0; index < varieties.length; index++) {
      const variety_forecast = forecastResults[index] as any;
      if (variety_forecast) {
        variety_forecasts.push(variety_forecast);
        if (duration === 'monthly') {
          console.log(
            `Monthly total calculation - ISO week range: ${report_week_no} to ${report_last_week_no} (inclusive)`
          );
          console.log(`Variety ${index} forecast array length:`, variety_forecast.forecasts.length);

          // Calculate the actual week range (accounting for year boundaries)
          let currentWeek = report_week_no;
          const endWeek = report_last_week_no;
          let iterations = 0;
          const maxIterations = 60; // Safety limit

          // Loop through weeks inclusively
          while (iterations < maxIterations) {
            // Array index is week number - 1 (0-indexed)
            const arrayIndex = currentWeek - 1;
            const forecastValue = variety_forecast.forecasts[arrayIndex];
            console.log(
              `  ISO Week ${currentWeek} (array index ${arrayIndex}): forecast = ${forecastValue}`
            );
            total_forecasts += Number(forecastValue) || 0;

            // Break if we've reached the end week
            if (currentWeek === endWeek) {
              break;
            }

            // Move to next week, wrapping at year boundary
            currentWeek++;
            if (currentWeek > variety_forecast.forecasts.length) {
              currentWeek = 1; // Wrap to week 1
            }
            iterations++;
          }
          console.log(`  Total forecasts after variety ${index}: ${total_forecasts}`);
        } else {
          total_forecasts += variety_forecast.forecasts[past_no_of_weeks];
          if (Array.isArray(variety_forecast?.field_forecasts)) {
            variety_forecast.field_forecasts.forEach((field_forecast_obj: any) => {
              const field_name = Object.keys(field_forecast_obj)[0];
              if (
                fieldsWithData.find(
                  (f) => f.name === field_name && f.farm_section_id === Number(farm_section_id)
                )
              ) {
                const field_forecast = field_forecast_obj[field_name];
                if (field_forecast && Array.isArray(field_forecast.forecasts)) {
                  farm_section_totals.forecast += field_forecast.forecasts[past_no_of_weeks] || 0;
                }
              }
            });
          }
        }
      }

      const variety_harvest = harvestResults[index] as any;
      if (variety_harvest) {
        variety_harvests.push(variety_harvest);
        if (duration === 'monthly') {
          console.log(`Variety ${index} harvest array length:`, variety_harvest.harvests.length);

          // Calculate the actual week range (accounting for year boundaries)
          let currentWeek = report_week_no;
          const endWeek = report_last_week_no;
          let iterations = 0;
          const maxIterations = 60; // Safety limit

          // Loop through weeks inclusively
          while (iterations < maxIterations) {
            // Array index is week number - 1 (0-indexed)
            const arrayIndex = currentWeek - 1;
            const harvestValue = variety_harvest.harvests[arrayIndex];
            const rejectValue = variety_harvest.rejects[arrayIndex];
            console.log(
              `  ISO Week ${currentWeek} (array index ${arrayIndex}): harvest = ${harvestValue}, rejects = ${rejectValue}`
            );
            total_harvests += Number(harvestValue) || 0;
            total_rejects += Number(rejectValue) || 0;

            // Break if we've reached the end week
            if (currentWeek === endWeek) {
              break;
            }

            // Move to next week, wrapping at year boundary
            currentWeek++;
            if (currentWeek > variety_harvest.harvests.length) {
              currentWeek = 1; // Wrap to week 1
            }
            iterations++;
          }
          console.log(
            `  Total harvests after variety ${index}: ${total_harvests}, rejects: ${total_rejects}`
          );
        } else {
          total_harvests += variety_harvest.harvests[past_no_of_weeks];
          if (Array.isArray(variety_forecast?.field_forecasts)) {
            variety_harvest.harvests.forEach((field_harvest_obj: any) => {
              const field_name = Object.keys(field_harvest_obj)[0];
              if (fieldsWithData) {
                const field_harvest = field_harvest_obj[field_name];
                if (field_harvest && Array.isArray(field_harvest.harvests)) {
                  farm_section_totals.harvest += field_harvest.harvests[past_no_of_weeks] || 0;
                }
              }
            });
          }
        }
      }

      const variety_milestone = milestoneResults[index] as any;
      if (variety_milestone) {
        variety_milestones.push(variety_milestone);
        if (duration === 'monthly') {
          let currentWeek = report_week_no;
          const endWeek = report_last_week_no;
          let iterations = 0;
          const maxIterations = 60;

          while (iterations < maxIterations) {
            const arrayIndex = currentWeek - 1;
            total_weeks_out_1 += variety_milestone.forecasts[arrayIndex]?.weeks_out_1 || 0;
            total_weeks_out_2 += variety_milestone.forecasts[arrayIndex]?.weeks_out_2 || 0;

            if (currentWeek === endWeek) break;

            currentWeek++;
            if (currentWeek > variety_milestone.forecasts.length) {
              currentWeek = 1;
            }
            iterations++;
          }
        }
      }

      const variety_demand_data = demandResults[index] as any;
      if (variety_demand_data) {
        variety_demand.push(variety_demand_data);
        if (duration === 'monthly') {
          let currentWeek = report_week_no;
          const endWeek = report_last_week_no;
          let iterations = 0;
          const maxIterations = 60;

          while (iterations < maxIterations) {
            const arrayIndex = currentWeek - 1;
            total_demand += variety_demand_data[arrayIndex]?.stems || 0;

            if (currentWeek === endWeek) break;

            currentWeek++;
            if (currentWeek > variety_demand_data.length) {
              currentWeek = 1;
            }
            iterations++;
          }
        } else {
          total_demand += variety_demand_data[past_no_of_weeks].stems;
        }
      }
    }

    setVarietyForecasts(variety_forecasts);
    setVarietyHarvests(variety_harvests);
    setVarietyMilestoneForecasts(variety_milestones);
    setVarietyDemand(variety_demand);
    setTotalFarmForecast(total_forecasts);
    setTotalFarmHarvest(total_harvests);
    setTotalFarmRejects(total_rejects);
    setTotalFarmDemand(total_demand);

    setFarmTotals({
      forecast: total_forecasts,
      harvest: total_harvests,
      demand: total_demand,
      rejects: total_rejects,
      weeks_out_1: total_weeks_out_1,
      weeks_out_2: total_weeks_out_2,
      active_fields_count: fieldsWithData.length,
      total_fields_count: farm?.number_of_active_fields || 0
    });
    setFarmSectionTotals({
      ...farm_section_totals,
      active_fields_count: fieldsWithData.filter(
        (f) => f.farm_section_id === Number(farm_section_id)
      ).length,
      total_fields_count: fields.filter((f) => f.farm_section_id === Number(farm_section_id)).length
    });

    // Update progress for data processing
    updateProgress();

    // Progress will be completed after graph preparation (in useEffect)
    setProgressPercentage(95);
  };

  // New function for farm and per-variety graph data
  const prepareFarmAndVarietyGraphData = () => {
    const farmGraphData: any[] = [];
    const farmSectionGraphData: any[] = [];
    const farmMilestoneGraphData: any[] = [];
    const varietiesGraphData: any[] = [];

    const numWeeks =
      duration === 'weekly'
        ? next_no_of_weeks + past_no_of_weeks
        : moment(dateString, 'DD-MMM-YYYY').isoWeeksInYear();

    // Prepare farm-level graph data
    for (let week_index = 0; week_index <= numWeeks; week_index++) {
      const farmWeekItem: any = {
        name:
          duration === 'weekly'
            ? 'Week ' +
              moment(dateString, 'DD-MMM-YYYY')
                .add(week_index - past_no_of_weeks, 'weeks')
                .isoWeek()
            : moment(dateString, 'DD-MMM-YYYY')
                .startOf('year')
                .add(week_index, 'weeks')
                .startOf('isoWeek')
                .isoWeek(),
        Forecasts: 0,
        Actuals: 0,
        Demand: 0
      };

      varietyForecasts.forEach((varietyForecast, varietyIndex) => {
        farmWeekItem.Forecasts += varietyForecast.forecasts[week_index] || 0;
        if (varietyHarvests[varietyIndex]) {
          farmWeekItem.Actuals += varietyHarvests[varietyIndex].harvests[week_index] || 0;
        }
        if (varietyDemand[varietyIndex]) {
          farmWeekItem.Demand += varietyDemand[varietyIndex][week_index]?.stems ?? 0;
        }
      });

      // Build up farm section graph data based on fields that belong to the selected farm section
      farmGraphData.push(farmWeekItem);

      // Prepare farm section graph data
      if (farmSection) {
        // Only include fields that belong to the selected farm section
        const sectionWeekItem: any = {
          name: farmWeekItem.name,
          Forecasts: 0,
          Actuals: 0,
          Demand: 0
        };

        fields
          .filter((field) => field.farm_section_id === farmSection.id)
          .forEach((field) => {
            // Find the corresponding variety index for this field
            const varietyIndex = varieties.findIndex((v) => v.id === field.variety_id);

            if (varietyIndex !== -1) {
              sectionWeekItem.Forecasts +=
                varietyForecasts[varietyIndex]?.field_forecasts?.find(
                  (ff: any) => Object.keys(ff)[0] === field.name
                )?.[field.name]?.forecasts?.[week_index] ?? 0;
              sectionWeekItem.Actuals +=
                varietyHarvests[varietyIndex]?.field_harvests?.find(
                  (fh: any) => Object.keys(fh)[0] === field.name
                )?.[field.name]?.harvests?.[week_index] ?? 0;
              sectionWeekItem.Demand += varietyDemand[varietyIndex]?.[week_index]?.stems ?? 0;
            }
          });

        farmSectionGraphData.push(sectionWeekItem);
      }
    }

    // Prepare farm-level milestone graph data
    for (let week_index = reportWeekNo - 1; week_index < reportLastWeekNo; week_index++) {
      // for (let week_index = 0; week_index <= numWeeks; week_index++) {
      const farmMilestoneWeekItem: any = {
        name:
          duration === 'weekly'
            ? 'Week ' +
              moment(dateString, 'DD-MMM-YYYY')
                .add(week_index - past_no_of_weeks, 'weeks')
                .isoWeek()
            : moment(dateString, 'DD-MMM-YYYY')
                .startOf('year')
                .add(week_index, 'weeks')
                .startOf('isoWeek')
                .isoWeek(),
        Forecasts: 0,
        Actuals: 0,
        weeks_out_1: 0,
        weeks_out_2: 0
      };

      varietyForecasts.forEach((varietyForecast, varietyIndex) => {
        farmMilestoneWeekItem.Forecasts += varietyForecast.forecasts[week_index] || 0;
        if (varietyHarvests[varietyIndex]) {
          farmMilestoneWeekItem.Actuals += varietyHarvests[varietyIndex].harvests[week_index] || 0;
        }
        if (varietyMilestoneForecasts[varietyIndex]) {
          farmMilestoneWeekItem.weeks_out_1 +=
            varietyMilestoneForecasts[varietyIndex].forecasts[week_index]?.weeks_out_1 ?? 0;
          farmMilestoneWeekItem.weeks_out_2 +=
            varietyMilestoneForecasts[varietyIndex].forecasts[week_index]?.weeks_out_2 ?? 0;
        }
      });

      farmMilestoneGraphData.push(farmMilestoneWeekItem);
    }

    // Prepare per-variety graph data
    varietyForecasts.forEach((varietyForecast, varietyIndex) => {
      const varietyWeekData: any[] = [];
      for (let week_index = 0; week_index <= numWeeks; week_index++) {
        const varietyWeekItem: any = {
          name:
            duration === 'weekly'
              ? 'Week ' +
                moment(dateString, 'DD-MMM-YYYY')
                  .add(week_index - past_no_of_weeks, 'weeks')
                  .isoWeek()
              : moment(dateString, 'DD-MMM-YYYY')
                  .startOf('year')
                  .add(week_index, 'weeks')
                  .startOf('isoWeek')
                  .isoWeek(),
          Forecasts: varietyForecast.forecasts[week_index] || 0,
          Actuals: varietyHarvests[varietyIndex]?.harvests[week_index] ?? 0,
          Demand: varietyDemand[varietyIndex]?.[week_index]?.stems ?? 0
        };

        varietyWeekData.push(varietyWeekItem);
      }

      const variety = varieties[varietyIndex];
      varietiesGraphData.push({
        variety_id: varietyForecast.query_metadata.variety_id,
        variety_name: varietyForecast.variety_name,
        variety_type: variety?.type || 'standard',
        colors: variety?.colors || [],
        accuracy: calculatePercentageOfValues(
          varietyForecast?.forecasts
            .slice(reportWeekNo - 1, reportLastWeekNo)
            .reduce((acc: number, curr: number) => acc + curr, 0),
          varietyHarvests[varietyIndex]?.harvests
            .slice(reportWeekNo - 1, reportLastWeekNo)
            .reduce((acc: number, curr: number) => acc + curr, 0)
            .toFixed(1)
        ),
        data: varietyWeekData
      });
    });

    setGraphData({
      farm: farmGraphData,
      section: farmSectionGraphData,
      varieties: varietiesGraphData,
      milestone: farmMilestoneGraphData
    });
  };

  useEffect(() => {
    // prepareVarietyGraphData();
    prepareFarmAndVarietyGraphData();

    // Complete the progress when graph data is ready
    if (varietyForecasts.length > 0 && isGeneratingReport) {
      setProgressPercentage(100);
      // Calculate generation time
      const endTime = Date.now();
      const durationMs = endTime - generationStartTime;
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      setGenerationTime(minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`);

      // Small delay to show 100% before hiding loading
      setTimeout(() => {
        setIsGeneratingReport(false);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varietyForecasts, varietyMilestoneForecasts, varietyHarvests, varietyDemand]);
  useEffect(() => {
    const field_forecasts_array: Record<string, FieldForecast> = fields.reduce((acc, field) => {
      acc[field.name] = {
        forecasts: Array(past_no_of_weeks + next_no_of_weeks + 1).fill(0),
        // harvests: null,
        graph_data: [],
        detailed_view: []
      };
      return acc;
    }, {} as Record<string, FieldForecast>);
    varietyForecasts.forEach((varietyForecast) => {
      const varietyHarvest = varietyHarvests.filter(
        (varietyHarvest) =>
          varietyHarvest.query_metadata.variety_id === varietyForecast.query_metadata.variety_id
      )[0];

      // Process fields with forecasts
      varietyForecast.field_forecasts.forEach((field_forecasts: any) => {
        const field_forecast_object: FieldForecast = Object.values(
          field_forecasts
        )[0] as FieldForecast;

        // add field harvests for varietyHarvest to field forecast object
        const field_harvests = varietyHarvest?.field_harvests?.find(
          (field_harvests: any) =>
            Object.keys(field_harvests)[0] === Object.keys(field_forecasts)[0]
        );

        //prepare field graph data
        const field_graph_data: any[] = [];
        for (let week_index = 0; week_index <= next_no_of_weeks + past_no_of_weeks; week_index++) {
          const graph_data_week_item: any = {
            name:
              'Week ' +
              moment(dateString, 'DD-MMM-YYYY')
                .add(week_index - past_no_of_weeks, 'weeks')
                .isoWeek()
          };

          Object.keys(varietyForecast?.production_category_ids).forEach(
            (production_category_id: any) => {
              graph_data_week_item[
                varietyForecast?.production_category_ids[production_category_id].replace(/\s/g, '')
              ] = field_forecast_object?.detailed_view
                ? field_forecast_object?.detailed_view[week_index][production_category_id] ?? 2
                : 1;
            }
          );

          // console.log('field_harvests', field_harvests, week_index);
          graph_data_week_item['Forecasts'] = field_forecast_object?.forecasts[week_index] ?? 0;
          // Get the first object in field_harvests (if exists)
          const firstFieldHarvest =
            field_harvests && typeof field_harvests === 'object'
              ? field_harvests[Object.keys(field_harvests)[0]]
              : undefined;
          graph_data_week_item['Actuals'] = firstFieldHarvest?.harvests?.[week_index] ?? 0;
          field_graph_data.push(graph_data_week_item);
        }

        // create field_forecast_object
        field_forecasts_array[
          Object.keys(field_forecasts)[0] + '_' + varietyForecast.query_metadata.variety_id
        ] = {
          ...(Object.values(field_forecasts)[0] as FieldForecast),
          harvests: field_harvests
            ? field_harvests[Object.keys(field_harvests)[0]].harvests
            : undefined,
          graph_data: field_graph_data
        };
      });

      // Also process fields that only have harvests (no forecasts)
      if (varietyHarvest?.field_harvests) {
        varietyHarvest.field_harvests.forEach((field_harvest: any) => {
          const fieldName = Object.keys(field_harvest)[0];
          const fieldKey = fieldName + '_' + varietyForecast.query_metadata.variety_id;

          // Only add if this field wasn't already processed (no forecast data)
          if (!field_forecasts_array[fieldKey]) {
            const field_graph_data: any[] = [];
            for (
              let week_index = 0;
              week_index <= next_no_of_weeks + past_no_of_weeks;
              week_index++
            ) {
              const graph_data_week_item: any = {
                name:
                  'Week ' +
                  moment(dateString, 'DD-MMM-YYYY')
                    .add(week_index - past_no_of_weeks, 'weeks')
                    .isoWeek(),
                Forecasts: 0,
                Actuals: field_harvest[fieldName]?.harvests?.[week_index] ?? 0
              };
              field_graph_data.push(graph_data_week_item);
            }

            field_forecasts_array[fieldKey] = {
              forecasts: Array(past_no_of_weeks + next_no_of_weeks + 1).fill(0),
              harvests: field_harvest[fieldName]?.harvests,
              graph_data: field_graph_data,
              detailed_view: []
            };
          }
        });
      }
    });

    setFieldForecasts(field_forecasts_array);

    // remove fields from the fields array that dont have forecasts or harvests in the field_forecasts_array
    const fields_with_data = fields.filter((field) => {
      // Exclude fields that:
      // - have all forecasts as 0 AND have null harvests (no data at all)
      // Include fields if they have either forecasts OR harvests
      const fieldKey = field.name + '_' + field.variety_id;
      const fieldData = field_forecasts_array[fieldKey];

      if (!fieldData) {
        return false;
      }

      const totalForecasts = fieldData.forecasts.reduce((acc, forecast) => acc + forecast, 0);
      const hasForecasts = totalForecasts > 0;
      const hasHarvests = fieldData.harvests !== null && fieldData.harvests !== undefined;

      // Include field if it has either forecasts or harvests
      return hasForecasts || hasHarvests;
    });
    setFieldsWithData(fields_with_data);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, varietyForecasts.length, varietyHarvests]);

  useEffect(() => {
    // clear toast when component unmounts
    return () => {
      toast.closeAll();
    };
  });

  useEffect(() => {
    // prepare graph data for weather
    const weather_graph_data: any[] = [];
    weatherDays.forEach((weatherDay: moment.Moment, day_index: number) => {
      const graph_data_item: any = {
        name: weatherDay.format('ddd DD')
      };

      if (weatherData?.weather && weatherData?.weather[day_index]) {
        graph_data_item['Min'] = weatherData.weather[day_index]?.mintempC;
        graph_data_item['Max'] = weatherData.weather[day_index].maxtempC;
        graph_data_item['rainfall'] = weatherData.weather[day_index].hourly[0].precipMM;
      }
      weather_graph_data.push(graph_data_item);
    });
    setWeatherGraphData(weather_graph_data);

    if (isArray(weatherData?.weather)) {
      prepareWeeklyWeatherOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherData, weatherDays]);

  const prepareWeeklyWeatherOverview = () => {
    const today = moment();
    // Initialize variables to track metrics
    const week_overview: {
      dates: string;
      maxTempC: { avg: number; max: number; min: number; total: number; count: number };
      minTempC: { avg: number; max: number; min: number; total: number; count: number };
      precipMM: { avg: number; max: number; min: number; total: number; count: number };
      humidity: { avg: number; max: number; min: number; total: number; count: number };
    }[] = [
      {
        dates: `${today.startOf('isoWeek').add(1, 'week').format('DD/MM')} - ${today
          .endOf('isoWeek')
          .format('DD/MM')}`,
        maxTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[0]['maxtempC']),
          total: 0,
          count: 0
        },
        minTempC: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0]['mintempC'],
          total: 0,
          count: 0
        },
        precipMM: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0].hourly[0].precipMM,
          total: 0,
          count: 0
        },
        humidity: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0].hourly[0].humidity,
          total: 0,
          count: 0
        }
      },
      {
        dates: `${today.add(1, 'week').startOf('isoWeek').format('DD/MM')} - ${today
          .endOf('isoWeek')
          .format('DD/MM')}`,
        maxTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[7]['maxtempC']),
          total: 0,
          count: 0
        },
        minTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[7]['mintempC']),
          total: 0,
          count: 0
        },
        precipMM: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[7]?.hourly[0].precipMM,
          total: 0,
          count: 0
        },
        humidity: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[7]?.hourly[0].humidity,
          total: 0,
          count: 0
        }
      }
    ];

    // Loop through each day in the weather data
    // weatherData.weather.forEach((day: any) => {
    for (let index = 0; index < weatherData?.weather.length; index++) {
      let week_index = 0;
      if (index > 6) {
        week_index = 1;
      }
      // Process daily values for maxTempC, minTempC, and humidity
      const maxtempC = parseFloat(weatherData.weather[index]['maxtempC']);
      const mintempC = parseFloat(weatherData.weather[index]['mintempC']);
      const humidity = parseFloat(weatherData.weather[index].hourly[0].humidity);
      const precipMM = parseFloat(weatherData.weather[index].hourly[0].precipMM);

      // Update max/min/average values for daily maxTempC, minTempC, and humidity
      week_overview[week_index].maxTempC.total += maxtempC;
      week_overview[week_index].maxTempC.count += 1;
      week_overview[week_index].maxTempC.max = Math.max(
        week_overview[week_index].maxTempC.max,
        maxtempC
      );
      week_overview[week_index].maxTempC.min = Math.min(
        week_overview[week_index].maxTempC.min,
        maxtempC
      );

      week_overview[week_index].minTempC.total += mintempC;
      week_overview[week_index].minTempC.count += 1;
      week_overview[week_index].minTempC.max = Math.max(
        week_overview[week_index].minTempC.max,
        mintempC
      );
      week_overview[week_index].minTempC.min = Math.min(
        week_overview[week_index].minTempC.min,
        mintempC
      );

      week_overview[week_index].humidity.total += humidity;
      week_overview[week_index].humidity.count += 1;
      week_overview[week_index].humidity.max = Math.max(
        week_overview[week_index].humidity.max,
        humidity
      );
      week_overview[week_index].humidity.min = Math.min(
        week_overview[week_index].humidity.min,
        humidity
      );

      week_overview[week_index].precipMM.total += precipMM;
      week_overview[week_index].precipMM.count += 1;
      week_overview[week_index].precipMM.max = Math.max(
        week_overview[week_index].precipMM.max,
        precipMM
      );
      week_overview[week_index].precipMM.min = Math.min(
        week_overview[week_index].precipMM.min,
        precipMM
      );

      // // Process hourly data for precipMM
      // day.hourly.forEach((hour) => {
      //   const precip = parseFloat(hour.precipMM);
      //   precipMM.total += precip;
      //   precipMM.count += 1;
      //   precipMM.max = Math.max(precipMM.max, precip);
      //   precipMM.min = Math.min(precipMM.min, precip);
      // });

      // Calculate averages for each category
      if (index % 7 == 0) {
        week_overview[week_index].maxTempC.avg =
          week_overview[week_index].maxTempC.total / week_overview[week_index].maxTempC.count;

        week_overview[week_index].minTempC.avg =
          week_overview[week_index].minTempC.total / week_overview[week_index].minTempC.count;
        week_overview[week_index].humidity.avg =
          week_overview[week_index].humidity.total / week_overview[week_index].humidity.count;
        week_overview[week_index].precipMM.avg =
          week_overview[week_index].precipMM.total / week_overview[week_index].precipMM.count;
      }
    }

    setWeatherWeekOverview(week_overview);
  };

  const calculateDays = (start_date: moment.Moment) => {
    const today = moment().startOf('day');
    const end_date = start_date.clone().add(14, 'days').endOf('day');
    let future_days = 0;
    let past_start_day = null;
    let past_end_day = null;

    for (let date = start_date.clone(); date.isBefore(end_date); date.add(1, 'day')) {
      if (date.isSameOrAfter(today)) {
        future_days++;
      } else {
        if (!past_start_day) {
          past_start_day = date.clone();
        }
        past_end_day = date.clone();
      }
    }

    return { future_days, past_start_day, past_end_day };
  };

  const ReportSchema = Yup.object().shape({
    report_date: Yup.date().typeError('Select date').required('Date is required')
  });

  const generateReport = (
    values: { report_date: Date | null; farm_section_id: number | null },
    { setSubmitting }: any
  ) => {
    if (farm && varieties.length > 0 && values.report_date) {
      let report_week_no = 0;
      if (duration === 'weekly') {
        report_week_no = moment(values.report_date).isoWeek();
      } else {
        // For monthly: find the first ISO week with more than 3 days in the target month
        const targetMonth = moment(values.report_date).month();
        const year = moment(values.report_date).year();
        // Start from the first day of the month
        const firstDayOfMonth = moment(values.report_date).startOf('month');
        console.log(
          'First day of month:',
          firstDayOfMonth.format('YYYY-MM-DD'),
          'Week:',
          firstDayOfMonth.isoWeek()
        );
        // Find the first ISO week that overlaps with the month
        let weekNo = firstDayOfMonth.isoWeek();
        let foundWeek = false;
        // Check up to 2 weeks from the start of the month
        for (let i = 0; i < 2; i++) {
          // Get all days in the week
          const weekStart = moment(firstDayOfMonth).isoWeek(weekNo).startOf('isoWeek');
          const weekDays = Array.from({ length: 7 }, (_, d) => moment(weekStart).add(d, 'days'));
          // Count how many days are in the target month
          const daysInMonth = weekDays.filter(
            (day) => day.month() === targetMonth && day.year() === year
          ).length;
          console.log(`Checking first week ${weekNo}: ${daysInMonth} days in target month`);
          if (daysInMonth > 3) {
            report_week_no = weekNo;
            foundWeek = true;
            console.log(`Found first week: ${weekNo}`);
            break;
          }
          weekNo++;
        }
        // Fallback: use start of month week if not found
        if (!foundWeek) {
          report_week_no = firstDayOfMonth.isoWeek();
          console.log(`Fallback first week: ${report_week_no}`);
        }
      }
      setReportWeekNo(report_week_no);

      let report_last_week_no = 0;
      if (duration === 'weekly') {
        report_last_week_no = moment(values.report_date).add(1, 'weeks').isoWeek();
      } else {
        // For monthly: find the last ISO week with more than 3 days in the target month
        const targetMonth = moment(values.report_date).month();
        const year = moment(values.report_date).year();
        // Start from the last day of the month
        const lastDayOfMonth = moment(values.report_date).endOf('month');
        console.log(
          'Last day of month:',
          lastDayOfMonth.format('YYYY-MM-DD'),
          'Week:',
          lastDayOfMonth.isoWeek()
        );
        // Find the last ISO week that overlaps with the month
        let weekNo = lastDayOfMonth.isoWeek();
        let foundWeek = false;

        // Check current week and previous weeks
        for (let i = 0; i < 3; i++) {
          // Skip invalid week numbers (week 0 or negative)
          if (weekNo <= 0) {
            // If we've gone below week 1, check week 52/53 of the previous year
            const weeksInPrevYear = moment(lastDayOfMonth).subtract(1, 'week').isoWeeksInYear();
            weekNo = weeksInPrevYear;
          }

          // Get all days in this ISO week
          const weekStart = moment(lastDayOfMonth).year(year).isoWeek(weekNo).startOf('isoWeek');
          const weekDays = Array.from({ length: 7 }, (_, d) => moment(weekStart).add(d, 'days'));
          // Count how many days are in the target month and year
          const daysInMonth = weekDays.filter(
            (day) => day.month() === targetMonth && day.year() === year
          ).length;
          console.log(
            `Checking week ${weekNo}: ${daysInMonth} days in target month (${
              targetMonth + 1
            }/${year})`
          );

          if (daysInMonth > 3) {
            report_last_week_no = weekNo;
            foundWeek = true;
            console.log(
              `Found last week: ${weekNo}, setting report_last_week_no to ${report_last_week_no}`
            );
            break;
          }
          weekNo--;
        }

        // Fallback: use the week before the invalid week
        if (!foundWeek) {
          // Get the number of weeks in the current year
          const weeksInYear = moment(lastDayOfMonth).isoWeeksInYear();
          report_last_week_no = weeksInYear;
          console.log(
            `Fallback: setting report_last_week_no to ${report_last_week_no} (last week of year)`
          );
        }
      }

      setReportLastWeekNo(report_last_week_no);
      setDateString(
        moment(values.report_date).format(duration == 'weekly' ? 'DD-MMM-YYYY' : 'MMMM YYYY')
      );
      setFarmSection(farmSections.find((section) => section.id == values.farm_section_id) || null);
      fetchFieldsData('');
      fetchFarmForecasts(
        values.report_date,
        report_week_no,
        report_last_week_no,
        Number(values.farm_section_id)
      );

      const first_day = moment(values.report_date);
      const { future_days, past_start_day, past_end_day } = calculateDays(first_day);
      // Create an array of 7 days starting from the next week's Monday
      const daysArray: moment.Moment[] = [];

      for (let i = 0; i < 7; i++) {
        daysArray.push(first_day.clone().add(i, 'days'));
      }
      setWeatherDays(daysArray);

      if (duration === 'weekly') {
        fetchWeatherData(
          future_days,
          past_start_day?.format('YYYY-MM-DD'),
          past_end_day?.format('YYYY-MM-DD')
        );
      }

      setHidePdf(false);
    } else {
      // If validation fails, reset the generating state
      setIsGeneratingReport(false);
      setProgressPercentage(0);
    }
    setSubmitting(false);
  };

  const isMonday = (date: Date) => {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    return day === 1; // Disable Mondays
  };

  const shakeButton = keyframes`
    0% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
     50% { transform: translateX(10px); }
    75% { transform: translateX(-10px); }
    100% { transform: translateX(0); }
  `;

  const [animationKey, setAnimationKey] = useState<number>(0);

  const handleTextClick = () => {
    setAnimationKey((prevKey) => prevKey + 1);
  };

  const durationList = ['Weekly', 'Monthly'];
  const [selectedDuration, setSelectedDuration] = useState<string>('weekly');
  const [duration, setDuration] = useState<string>('weekly');
  const onSelectDuration = () => {
    return (selection: number | number[]) => {
      switch (selection) {
        case 0:
          setDuration('weekly');
          break;
        case 1:
          setDuration('monthly');
          break;
        default:
          break;
      }
    };
  };

  useEffect(() => {
    setHidePdf(true);
    setIsGeneratingReport(false);
    setProgressPercentage(0);
    setGenerationTime('');
    setBatchProgress('');
  }, [farm, duration, selectedDuration]);
  useEffect(() => {
    if (farm) fetchFarmSections(farm, '');
  }, [farm]);

  const [varietyGridConfig, setVarietyGridConfig] = useState<any>('46%'); // Default width for variety graphs
  useEffect(() => {
    const varieties_count = varieties.length;
    const varietyGridConfig: any = {
      items_per_page: 0,
      items_per_row: 0,
      item_width: '46%',
      item_height: '46%',
      number_of_pages: 1
    };
    // calculate variety page config based on number of varieties
    if (varieties_count % 5 === 0 || varieties_count > 15) {
      varietyGridConfig.item_width = '18%';
      varietyGridConfig.items_per_row = 5;
    } else if (varieties_count % 4 === 0) {
      varietyGridConfig.item_width = '21%';
      varietyGridConfig.items_per_row = 4;
    } else if (varieties_count % 3 === 0) {
      varietyGridConfig.items_per_row = 3;
      varietyGridConfig.item_width = '30%';
    } else if (varieties_count % 2 === 0) {
      varietyGridConfig.items_per_row = 2;
      varietyGridConfig.item_width = '46%';
    } else {
      // Odd numbers not divisible by 3, 4, or 5
      varietyGridConfig.items_per_row = Math.min(4, varieties_count);
      varietyGridConfig.item_width = 100 / varietyGridConfig.items_per_row - 2 + '%';
    }

    // Calculate items per page and number of pages (max 15 items per page)
    varietyGridConfig.items_per_page = Math.min(15, varieties_count);
    varietyGridConfig.number_of_pages = Math.ceil(varieties_count / 15);
    varietyGridConfig.rows_per_page = Math.ceil(
      varietyGridConfig.items_per_page / varietyGridConfig.items_per_row
    );
    varietyGridConfig.item_height = 93 / varietyGridConfig.rows_per_page + '%';
    setVarietyGridConfig(varietyGridConfig);
  }, [varieties.length]);
  return (
    <Box
      width="100%"
      backgroundColor="white"
      boxShadow="0px 1px 4px rgba(12, 12, 13, 0.15)"
      padding={6}
      borderRadius={8}
      height={'85vh'}
    >
      <Flex justifyContent="space-between" alignItems="baseline" padding={0}>
        <Box>
          <Heading
            data-cy="production-plan-heading"
            as="h4"
            mb="2"
            fontSize="1.35rem"
            fontWeight="normal"
          >
            PDF Reporting
          </Heading>
          <Text as="p">{farm ? `${farm?.name}, ${farm?.city}` : <Skeleton count={1} />}</Text>
        </Box>
        <Flex flexDir="row">
          <FilterDropDown
            label={`${duration}`}
            items={durationList}
            selectedItem={selectedDuration}
            onSelectFilter={onSelectDuration()}
            name="farm-production"
            LeftIcon={Calendar}
            disabled={isProcessing}
          />
          <Flex flexDir="column" marginStart={4} alignItems="flex-end">
            <Formik
              initialValues={{ report_date: null, farm_section_id: null }}
              validationSchema={ReportSchema}
              onSubmit={generateReport}
            >
              {({ setFieldValue, errors, touched, isSubmitting }) => (
                <Form>
                  <Flex flexDir={'row'}>
                    <Flex flexDir={'row'}>
                      <Box mr={4} minW="180px">
                        <Field name="farm_section_id">
                          {({ field, form }: any) => (
                            <Select
                              {...field}
                              id="farm_section_id"
                              style={{
                                marginLeft: 6,
                                marginRight: 10,
                                padding: '4px 8px',
                                borderRadius: 4,
                                border: '1px solid #e2e8f0',
                                minWidth: 120
                              }}
                              disabled={isProcessing}
                              size={'sm'}
                              onChange={(e) => {
                                form.setFieldValue('farm_section_id', e.target.value);
                                // Optionally, trigger any farm change logic here
                              }}
                              placeholder="Farm Section..."
                            >
                              {Array.isArray(farmSections) ? (
                                farmSections.map((f: any) => (
                                  <option key={f.id} value={f.id}>
                                    {f.name}
                                  </option>
                                ))
                              ) : (
                                <option value={farm?.id}>{farm?.name}</option>
                              )}
                            </Select>
                          )}
                        </Field>
                      </Box>
                      {duration === 'monthly' && (
                        <Box mr={4} minW="100px">
                          <Field name="date_range_type">
                            {({ field, form }: any) => (
                              <Select
                                {...field}
                                id="date_range_type"
                                style={{
                                  marginLeft: 6,
                                  marginRight: 10,
                                  padding: '4px 8px',
                                  borderRadius: 4,
                                  border: '1px solid #e2e8f0',
                                  minWidth: 100
                                }}
                                disabled={isProcessing}
                                size={'sm'}
                                onChange={(e) => {
                                  form.setFieldValue('date_range_type', e.target.value);
                                }}
                                defaultValue={'iso_Weeks'}
                              >
                                <option value={'iso_Weeks'}>ISO Weeks</option>
                                {/* <option value={'days_of_month'}>Days Of Month</option> */}
                              </Select>
                            )}
                          </Field>
                        </Box>
                      )}
                    </Flex>
                    <Flex flexDir={'row'}>
                      <Text as="label" mt={1}>
                        Date:
                      </Text>
                      <Tooltip
                        hasArrow
                        shouldWrapChildren
                        label={'Select Start of Week'}
                        transition={'all .1s ease-in-out'}
                        placement="top"
                      >
                        <Field name="report_date">
                          {({ field }: { field: Date }) => (
                            <DatePicker
                              {...field}
                              id="report_date"
                              isoWeek
                              showWeekNumbers
                              format={'yyyy-MM' + (duration == 'weekly' ? '-dd' : '')}
                              shouldDisableDate={(date: Date) => !isMonday(date)}
                              aria-autocomplete="none"
                              editable={false}
                              style={{
                                marginRight: 10,
                                marginLeft: 6,
                                width: '120%'
                              }}
                              oneTap={true}
                              placement="bottom"
                              size="sm"
                              onChange={(date) => setFieldValue('report_date', date)}
                            />
                          )}
                        </Field>
                      </Tooltip>
                    </Flex>
                    <Button
                      key={animationKey}
                      width="100px"
                      height={'30px'}
                      theme="primary"
                      size={'sm'}
                      type="submit"
                      pb={2}
                      pt={2}
                      disabled={
                        isProcessing ||
                        isGeneratingReport ||
                        !variety?.id ||
                        !farm?.id ||
                        isSubmitting
                          ? true
                          : false
                      }
                      animation={`${shakeButton} 0.5s ease-in-out`}
                    >
                      Generate
                      {(isProcessing || isGeneratingReport) && (
                        <>
                          <Box px={1.5}></Box>
                          <Spinner color="white.500" size="sm" />
                        </>
                      )}
                    </Button>
                  </Flex>
                  {errors.report_date && touched.report_date ? (
                    <Text color="red.500" mt={1} ml={2}>
                      {errors.report_date}
                    </Text>
                  ) : null}
                </Form>
              )}
            </Formik>
            {!isProcessing && !isGeneratingReport && variety?.id && farm?.id && !hidePdf && (
              <>
                <Flex direction={'row-reverse'} mt={2}>
                  <PDFDownloadLink
                    document={
                      duration === 'monthly' ? (
                        <MonthlyPDFReport
                          farm={farm}
                          varieties={varieties}
                          week_no={reportWeekNo}
                          last_week_no={reportLastWeekNo}
                          fields={fieldsWithData.sort((a, b) => a.name.localeCompare(b.name))}
                          total_farm_forecast={totalFarmForecast}
                          total_farm_harvest={totalFarmHarvest}
                          total_farm_rejects={totalFarmRejects}
                          graph_data={graphData}
                          variety_forecasts={varietyForecasts}
                          variety_harvests={varietyHarvests}
                          variety_demands={varietyDemand}
                          date_string={dateString}
                          field_forecasts={fieldForecasts}
                          total_farm_demand={totalFarmDemand}
                          farm_totals={farmTotals}
                        />
                      ) : (
                        <WeeklyPDFReport
                          farm={farm}
                          farmSection={farmSection}
                          varieties={varieties}
                          week_no={reportWeekNo}
                          fields={fieldsWithData.sort((a, b) => a.name.localeCompare(b.name))}
                          total_farm_forecast={totalFarmForecast}
                          graph_data={graphData}
                          variety_forecasts={varietyForecasts}
                          date_string={dateString}
                          field_forecasts={fieldForecasts}
                          weather_days={weatherDays}
                          weather_data={weatherData}
                          weather_graph_data={weatherGraphData}
                          weather_week_overview={weatherWeekOverview}
                          variety_grid_config={varietyGridConfig}
                          farm_section_totals={farmSectionTotals}
                        />
                      )
                    }
                    fileName={
                      duration === 'monthly'
                        ? `${farm.customer.name} -${farm.name} ${dateString} Report`
                        : `${farm.customer.name} -${farm.name} Week ${reportWeekNo} Forecast`
                    }
                    style={{
                      backgroundColor: '#00a896',
                      color: '#fff',
                      padding: 6,
                      borderTopRightRadius: 4,
                      borderTopLeftRadius: 4,
                      fontSize: 13,
                      marginBottom: 0
                    }}
                  >
                    <Icon as={Download} stroke={'white.500'} w={3} h={3} mr={1} />
                    Download Report
                  </PDFDownloadLink>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>

      {isGeneratingReport || isProcessing || !variety || !farm?.id ? (
        <Box
          backgroundColor={'gray.50'}
          height={'85%'}
          width={'100%'}
          borderRadius={8}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection={'column'}
        >
          <Text color={'primary.500'} textAlign={'center'}>
            <Spinner color="primary.500" size="xs" mr={1} />
            Generating your pdf report...
          </Text>
          <Progress
            hasStripe
            value={progressPercentage}
            width={300}
            size={'sm'}
            colorScheme={'teal'}
            mt={4}
            isAnimated={true}
            style={{ borderRadius: 4 }}
          />
          <Text color={'gray.400'} textAlign={'center'} fontSize="xs" mt={2}>
            {progressPercentage < 25
              ? 'Fetching forecast data...'
              : progressPercentage < 50
              ? 'Fetching harvest data...'
              : progressPercentage < 75
              ? duration === 'monthly'
                ? 'Fetching demand data...'
                : 'Processing data...'
              : progressPercentage < 95
              ? 'Processing data...'
              : 'Preparing report...'}
          </Text>
          {batchProgress && (
            <Text color={'gray.300'} textAlign={'center'} fontSize="xs" mt={1}>
              {batchProgress}
            </Text>
          )}
          {generationTime && (
            <Text color={'teal.600'} textAlign={'center'} fontSize="xs" mt={2} fontWeight="bold">
              Generated in {generationTime}
            </Text>
          )}
        </Box>
      ) : (
        <>
          {variety?.id && farm?.id && (
            <>
              {hidePdf ? (
                <Box
                  backgroundColor={'gray.50'}
                  height={'85%'}
                  width={'100%'}
                  borderRadius={8}
                  display="flex"
                  flexDir="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color={'gray.300'} textAlign={'center'}>
                    Click{' '}
                    <Link onClick={handleTextClick} textDecoration="underline">
                      Generate
                    </Link>{' '}
                    to view pdf report here
                  </Text>
                </Box>
              ) : (
                <PDFViewer
                  width={'100%'}
                  height={'85%'}
                  key={`${farm?.id}-${dateString}-${varieties.length}-${varietyForecasts.length}`}
                >
                  {duration == 'monthly' ? (
                    <MonthlyPDFReport
                      farm={farm}
                      varieties={varieties}
                      week_no={reportWeekNo}
                      last_week_no={reportLastWeekNo}
                      fields={fieldsWithData.sort((a, b) => a.name.localeCompare(b.name))}
                      total_farm_forecast={totalFarmForecast}
                      total_farm_harvest={totalFarmHarvest}
                      total_farm_rejects={totalFarmRejects}
                      total_farm_demand={totalFarmDemand}
                      // variety_graph_data={varietyGraphData}
                      graph_data={graphData}
                      variety_forecasts={varietyForecasts}
                      variety_harvests={varietyHarvests}
                      variety_demands={varietyDemand}
                      date_string={dateString}
                      field_forecasts={fieldForecasts}
                      farm_totals={farmTotals}
                    />
                  ) : (
                    <WeeklyPDFReport
                      farm={farm}
                      farmSection={farmSection}
                      varieties={varieties}
                      week_no={reportWeekNo}
                      fields={fieldsWithData.sort((a, b) => a.name.localeCompare(b.name))}
                      total_farm_forecast={totalFarmForecast}
                      graph_data={graphData}
                      variety_forecasts={varietyForecasts}
                      date_string={dateString}
                      field_forecasts={fieldForecasts}
                      weather_days={weatherDays}
                      weather_data={weatherData}
                      weather_graph_data={weatherGraphData}
                      weather_week_overview={weatherWeekOverview}
                      variety_grid_config={varietyGridConfig}
                      farm_section_totals={farmSectionTotals}
                    />
                  )}
                </PDFViewer>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PDFReporting;
